import { iconSvg } from './icon';
import { escapeHtml } from './util';

export interface DropdownOption {
  value: string;
  label: string;
  count?: number;
}

export interface DropdownConfig {
  ariaLabel: string;
  /** Multi-select keeps the panel open and toggles values; single closes on pick. */
  multi?: boolean;
  className?: string;
  onSelect: (value: string) => void;
}

export interface DropdownUpdate {
  options: DropdownOption[];
  selected: string[];
  triggerLabel: string;
  active?: boolean;
}

export interface DropdownHandle {
  el: HTMLElement;
  update: (u: DropdownUpdate) => void;
  close: () => void;
}

/**
 * A brand-styled, non-native dropdown (used for the mobile type + tags filters).
 * Trigger button + a floating listbox panel; closes on outside click / Escape.
 * Keyboard: Enter/Space toggle open, Escape closes and restores focus.
 */
export function createDropdown(config: DropdownConfig): DropdownHandle {
  const root = document.createElement('div');
  root.className = `dd${config.className ? ` ${config.className}` : ''}`;

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'dd-trigger';
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-label', config.ariaLabel);

  const panel = document.createElement('div');
  panel.className = 'dd-panel';
  panel.setAttribute('role', 'listbox');
  if (config.multi) panel.setAttribute('aria-multiselectable', 'true');
  panel.hidden = true;

  root.append(trigger, panel);

  let open = false;
  const onDoc = (e: MouseEvent) => {
    if (!root.contains(e.target as Node)) setOpen(false);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      trigger.focus();
    }
  };
  const opts = (): HTMLButtonElement[] => [...panel.querySelectorAll<HTMLButtonElement>('.dd-opt')];
  function setOpen(v: boolean): void {
    if (v === open) return;
    open = v;
    trigger.setAttribute('aria-expanded', String(v));
    panel.hidden = !v;
    root.classList.toggle('dd--open', v);
    if (v) {
      document.addEventListener('click', onDoc, true);
      document.addEventListener('keydown', onKey);
      // Move focus into the panel so it's keyboard-navigable from the off.
      const list = opts();
      (list.find((o) => o.getAttribute('aria-selected') === 'true') ?? list[0])?.focus();
    } else {
      document.removeEventListener('click', onDoc, true);
      document.removeEventListener('keydown', onKey);
    }
  }

  trigger.addEventListener('click', () => setOpen(!open));

  // Arrow / Home / End roving over the option buttons.
  panel.addEventListener('keydown', (e) => {
    const list = opts();
    if (!list.length) return;
    const i = list.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      list[(i + 1) % list.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      list[(i - 1 + list.length) % list.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      list[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      list[list.length - 1]?.focus();
    }
  });

  const update = ({ options, selected, triggerLabel, active }: DropdownUpdate): void => {
    const sel = new Set(selected);
    trigger.innerHTML =
      `<span class="dd-trigger-label">${escapeHtml(triggerLabel)}</span>` + iconSvg('chevron-down', 13);
    trigger.classList.toggle('dd-trigger--active', !!active);
    panel.innerHTML = options
      .map((o) => {
        const on = sel.has(o.value);
        return `<button type="button" class="dd-opt${on ? ' dd-opt--on' : ''}" role="option" aria-selected="${on}" data-value="${escapeHtml(o.value)}">
          <span class="dd-opt-label">${escapeHtml(o.label)}</span>
          ${o.count != null ? `<span class="dd-opt-count">${o.count}</span>` : ''}
          <span class="dd-opt-check" aria-hidden="true">${on ? '✓' : ''}</span>
        </button>`;
      })
      .join('');
    panel.querySelectorAll<HTMLButtonElement>('.dd-opt').forEach((btn) => {
      btn.addEventListener('click', () => {
        config.onSelect(btn.dataset.value!);
        if (!config.multi) {
          setOpen(false);
          trigger.focus();
        }
      });
    });
  };

  return { el: root, update, close: () => setOpen(false) };
}
